import { FilterMinimalStyle3 } from '@griply/assets';
import {
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterOptionKeys,
  OrderDirection,
} from '@griply/types';
import {
  FilterSectionDefinition,
  isDefaultFilterDocument,
} from '@griply/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilterMinimalStyleCircle18 } from 'shared/assets/icons';
import {
  Button,
  Colors as ButtonColors,
  Sizes,
  Variants,
} from 'shared/components/ui/button';
import { FilterContextMenu } from 'shared/components/ui/filter-context-menu';
import {
  Colors as IconButtonColors,
  IconButton,
} from 'shared/components/ui/icon-button';
import { ButtonVariants } from 'shared/components/ui/popup-menu';
import { FilterDocuments } from 'shared/constants';
import { useFilterState } from 'shared/hooks/use-filter-state';
import { useOnFilterOption } from 'shared/hooks/use-on-filter';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import {
  FilterButtonOption,
  FilterDateRangeOption,
  FilterOptionType,
} from 'shared/types/filter';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import {
  GoalSortingMode,
  GroupByMode,
  SortingMode,
} from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import * as Styled from './filter-button.style';

enum ExtraFilterOptions {
  CreateSmartList = 'createSmartList',
  UpdateSmartList = 'updateSmartList',
}

export type FilterButtonProps<
  GBM extends GroupByMode,
  SM extends SortingMode | GoalSortingMode,
  F extends FilterOptionKeys,
> = {
  filterDocument: FilterDocument<GBM, SM, F>;
  defaultFilterDocument?: FilterDocument<GBM, SM, F>;
  supportedGroupByModes?: GBM[];
  supportedSortingModes?: SM[];
  supportedFilters?: F[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  updateFilterDocument?: (filterDocument: FilterDocument<GBM, SM, F>) => void;
  onReset: () => void;
  title?: string;
  labelOverrides?: Partial<Record<GroupByMode, string>>;
  filterLabelOverrides?: Partial<Record<FilterOptionKeys, string>>;
  filterIconOverrides?: Partial<
    Record<FilterOptionKeys, NonNullable<FilterDateRangeOption['icon']>>
  >;
  filterInfoUrlOverrides?: Partial<Record<FilterOptionKeys, string | null>>;
  filterSections?: FilterSectionDefinition[];
  sortingTranslationKeys?: Partial<
    Record<SortingMode | GoalSortingMode, string>
  >;
  sortDirection?: OrderDirection;
  onSortDirectionChange?: (direction: OrderDirection) => void;
  hideNoGoal?: boolean;
  renderAsIconButton?: boolean;
  // Fixed icon color — also disables the darkened active state, so the button
  // blends with sibling header icons (the badge alone marks active filters).
  iconColor?: IconButtonColors;
  onCreateSmartList?: () => void;
  onUpdateSmartList?: () => void;
  preventDrag?: boolean;
};

export const FilterButton = <
  GBM extends GroupByMode,
  SM extends SortingMode | GoalSortingMode,
  F extends FilterOptionKeys,
>({
  filterDocument,
  defaultFilterDocument,
  supportedGroupByModes,
  supportedSortingModes,
  supportedFilters,
  goals,
  lifeAreas,
  tags,
  updateFilterDocument,
  onReset,
  title,
  labelOverrides,
  filterLabelOverrides,
  filterIconOverrides,
  filterInfoUrlOverrides,
  filterSections,
  sortingTranslationKeys,
  sortDirection,
  onSortDirectionChange,
  hideNoGoal,
  renderAsIconButton,
  iconColor,
  onCreateSmartList,
  onUpdateSmartList,
  preventDrag,
}: FilterButtonProps<GBM, SM, F>) => {
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const user = useUser();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
  const weekStartsOn = getWeekStartsOn(user);
  const {
    menuOpen: filterMenuOpen,
    openMenu: openFilterMenu,
    closeMenu: closeFilterMenu,
  } = useOpenMenu();

  const { filters, activeFilters, activeOptions } = useFilterState(
    filterDocument,
    {
      lifeAreas,
      tags,
      goals,
      dateFormat,
      weekStartsOn,
      defaultFilterDocument,
      supportedGroupByModes,
      supportedSortingModes,
      supportedFilters,
      labelOverrides,
      filterLabelOverrides,
      filterIconOverrides,
      filterInfoUrlOverrides,
      filterSections,
      sortingTranslationKeys,
      sortingDirection: sortDirection,
      hideNoGoal,
      lifeAreaSorting: user?.settings?.lifeAreaSorting,
    },
  );

  const onFilter = useOnFilterOption(
    filterDocument,
    defaultFilterDocument,
    updateFilterDocument,
  );

  const _onFilter = (options: {
    id: ID;
    value?: string | string[];
    durationFilter?: FilterDocumentDurationFilter | null;
    dateFilter?: FilterDocumentDateFilter | null;
  }) => {
    if (
      options.id === ExtraFilterOptions.CreateSmartList &&
      onCreateSmartList
    ) {
      closeFilterMenu();
      onCreateSmartList();
      return;
    }

    if (
      options.id === ExtraFilterOptions.UpdateSmartList &&
      onUpdateSmartList
    ) {
      closeFilterMenu();
      onUpdateSmartList();
      return;
    }

    // Ascending/Descending live inside the Sort select; route them to direction.
    if (
      options.id === DocumentOrganizationKeys.SortingMode &&
      onSortDirectionChange &&
      (options.value === OrderDirection.Asc ||
        options.value === OrderDirection.Desc)
    ) {
      onSortDirectionChange(options.value);
      return;
    }

    onFilter(options);
  };

  const enhancedFilters = React.useMemo(() => {
    const mapped = filters.map((group) => {
      if (
        group.id === FilterDocuments.ResetFilterDocumentID &&
        (onCreateSmartList || onUpdateSmartList)
      ) {
        const extraFilters: FilterButtonOption[] = [];

        if (onCreateSmartList) {
          extraFilters.push({
            id: ExtraFilterOptions.CreateSmartList,
            label: t(
              'context-menus.filtering.task.filters.create-smart-list.label',
            ),
            type: FilterOptionType.Button,
            buttonVariant: ButtonVariants.Outlined,
            disabled: !activeFilters,
          });
        }

        if (onUpdateSmartList) {
          const isDirty = !isDefaultFilterDocument(
            filterDocument,
            defaultFilterDocument,
            supportedFilters,
          );
          extraFilters.push({
            id: ExtraFilterOptions.UpdateSmartList,
            label: t(
              'context-menus.filtering.task.filters.update-smart-list.label',
            ),
            type: FilterOptionType.Button,
            buttonVariant: ButtonVariants.Outlined,
            disabled: !isDirty,
          });
        }

        return {
          ...group,
          filters: [...group.filters, ...extraFilters],
        };
      }
      return group;
    });

    return mapped;
  }, [
    onCreateSmartList,
    onUpdateSmartList,
    filters,
    t,
    activeFilters,
    filterDocument,
    defaultFilterDocument,
    supportedFilters,
  ]);

  const filterButtonLabel = activeFilters
    ? `${t('general.labels.filter')}: ${activeFilters}`
    : t('general.labels.filter');

  return (
    <>
      {renderAsIconButton ? (
        <Styled.IconButtonContainer $isActive={!iconColor && !!activeFilters}>
          <IconButton
            ref={filterButtonRef}
            icon={FilterMinimalStyle3}
            strokeWidth={2}
            onClick={openFilterMenu}
            highlighted={filterMenuOpen}
            color={
              iconColor ??
              (activeFilters
                ? IconButtonColors.Inherit
                : IconButtonColors.Default)
            }
            preventDrag={preventDrag}
          />
          <Styled.Badge $hidden={!activeFilters}>{activeFilters}</Styled.Badge>
        </Styled.IconButtonContainer>
      ) : (
        <Button
          ref={filterButtonRef}
          onClick={openFilterMenu}
          endIcon={FilterMinimalStyleCircle18}
          size={Sizes.Small}
          variant={Variants.Text}
          color={activeFilters ? ButtonColors.Primary : ButtonColors.Inherit}
          selected={!!activeFilters}
          preventDrag={preventDrag}
        >
          {filterButtonLabel}
        </Button>
      )}

      {filterMenuOpen && (
        <FilterContextMenu
          referenceElement={filterButtonRef}
          onOutside={closeFilterMenu}
          position="bottom-end"
          title={title ?? t('context-menus.filtering.task.title')}
          options={enhancedFilters}
          activeOptions={activeOptions}
          onOption={_onFilter}
          onReset={onReset}
        />
      )}
    </>
  );
};
